/* eslint-disable no-console */
/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
/**
 * Order Statuses Hook for ArendaPro+
 * 
 * Provides CRUD operations for order statuses using the new domain-specific table.
 * Replaces the generic tags system for order status management.
 * 
 * Following STYLEGUIDE.md patterns:
 * - Uses supabaseWorker for data operations
 * - Proper error handling and loading states
 * - React Query for caching and synchronization
 * - TypeScript strict typing
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabaseWorker } from '@/integrations/supabase/workerClient';
import { useAuth } from '@/context/AuthContext';
import { 
  OrderStatus, 
  OrderStatusDb, 
  OrderStatusUpdate,
  DomainStatusOption,
  DomainStatusFilters
} from '@/types/domain-statuses';
import {
  orderStatusDbToClient,
  orderStatusClientToDbInsert,
  orderStatusUpdateToDb,
  statusesToOptions,
  validateStatusName,
  validateColorCode,
  sanitizeStatusInput
} from '@/lib/mappers/domainStatusMappers';
import { toast } from 'sonner';
import { getNextStatusOrderValue } from './utils/orderHelpers';

// =============================================================================
// QUERY KEYS
// =============================================================================

const ORDER_STATUSES_QUERY_KEY = 'orderStatuses';

export const orderStatusesKeys = {
  all: [ORDER_STATUSES_QUERY_KEY] as const,
  lists: () => [...orderStatusesKeys.all, 'list'] as const,
  list: (filters: DomainStatusFilters) => [...orderStatusesKeys.lists(), filters] as const,
  details: () => [...orderStatusesKeys.all, 'detail'] as const,
  detail: (id: string) => [...orderStatusesKeys.details(), id] as const,
};

// =============================================================================
// MAIN HOOK
// =============================================================================

export type UseOrderStatusesOptions = {
  filters?: DomainStatusFilters;
  enabled?: boolean;
}

export function useOrderStatuses(hookOptions: UseOrderStatusesOptions = {}) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { filters = {}, enabled = true } = hookOptions;

  // =============================================================================
  // QUERIES
  // =============================================================================

  const {
    data: orderStatuses = [],
    isLoading,
    error,
    refetch
  } = useQuery({
    queryKey: orderStatusesKeys.list(filters),
    queryFn: async (): Promise<OrderStatus[]> => {
      if (!user?.id) {
        throw new Error('User not authenticated');
      }

      let query = supabaseWorker
        .from('order_statuses')
        .select('*')
        .eq('user_id', user.id)
        .order('sort_order', { ascending: true })
        .order('created_at', { ascending: true });

      if (filters.search) {
        query = query.ilike('name', `%${filters.search}%`);
      }

      const { data, error } = await query;

      if (error) {
        console.error('Error fetching order statuses:', error);
        const errMsg = (error as { message?: string | null } | null)?.message ?? 'Unknown error';
        throw new Error(`Failed to fetch order statuses: ${errMsg}`);
      }

      return (data as OrderStatusDb[]).map(orderStatusDbToClient);
    },
    enabled: enabled && !!user?.id,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });

  // =============================================================================
  // MUTATIONS
  // =============================================================================

  const createMutation = useMutation({
    mutationFn: async (newStatus: Omit<OrderStatus, 'id' | 'createdAt' | 'updatedAt' | 'userId'>): Promise<OrderStatus> => {
      if (!user?.id) {
        throw new Error('User not authenticated');
      }

      // Validate input
      const sanitized = sanitizeStatusInput(newStatus);
      
      if (!validateStatusName(sanitized.name || '')) {
        throw new Error('Название статуса должно содержать от 1 до 100 символов');
      }

      if (sanitized.color && !validateColorCode(sanitized.color)) {
        throw new Error('Некорректный формат цвета. Используйте HEX формат (например, #10b981)');
      }

      // Check for duplicates
      const existingStatus = orderStatuses.find(
        status => status.name.toLowerCase() === sanitized.name?.toLowerCase()
      );
      
      if (existingStatus) {
        throw new Error('Статус с таким названием уже существует');
      }

      const name = sanitized.name ?? newStatus.name?.trim() ?? '';
      if (!name) {
        throw new Error('Название статуса должно содержать от 1 до 100 символов');
      }

      const color = sanitized.color ?? newStatus.color ?? '#6b7280';
      const description = sanitized.description ?? newStatus.description?.trim();
      const nextOrder = await getNextStatusOrderValue('order_statuses', user.id);

      const insertPayload: Omit<OrderStatus, 'id' | 'createdAt' | 'updatedAt'> & { userId: string } = {
        name,
        color,
        sortOrder: nextOrder,
        userId: user.id,
        description: description ?? undefined,
      };

      const insertData = orderStatusClientToDbInsert(insertPayload);

      const { data, error } = await supabaseWorker
        .from('order_statuses')
        .insert(insertData)
        .select()
        .single();

      if (error) {
        console.error('Error creating order status:', error);
        const errMsg = (error as { message?: string | null } | null)?.message ?? 'Unknown error';
        throw new Error(`Failed to create order status: ${errMsg}`);
      }

      return orderStatusDbToClient(data as OrderStatusDb);
    },
    onSuccess: (newStatus) => {
      // Invalidate and refetch order statuses
      queryClient.invalidateQueries({ queryKey: orderStatusesKeys.all });
      toast.success(`Статус заказа "${newStatus.name}" создан`);
    },
    onError: (error: Error) => {
      console.error('Error creating order status:', error);
      toast.error(error.message || 'Ошибка при создании статуса заказа');
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: OrderStatusUpdate }): Promise<OrderStatus> => {
      if (!user?.id) {
        throw new Error('User not authenticated');
      }

      // Validate input
      const sanitized = sanitizeStatusInput(updates);

      if (sanitized.name && !validateStatusName(sanitized.name)) {
        throw new Error('Название статуса должно содержать от 1 до 100 символов');
      }

      if (sanitized.color && !validateColorCode(sanitized.color)) {
        throw new Error('Некорректный формат цвета. Используйте HEX формат (например, #10b981)');
      }

      // Check for duplicates (excluding current status)
      if (sanitized.name) {
        const existingStatus = orderStatuses.find(
          status => status.id !== id && status.name.toLowerCase() === sanitized.name?.toLowerCase()
        );
        
        if (existingStatus) {
          throw new Error('Статус с таким названием уже существует');
        }
      }

      const updateData = orderStatusUpdateToDb(sanitized);

      const { data, error } = await supabaseWorker
        .from('order_statuses')
        .update(updateData)
        .eq('id', id)
        .eq('user_id', user.id)
        .select()
        .single();

      if (error) {
        console.error('Error updating order status:', error);
        const errMsg = (error as { message?: string | null } | null)?.message ?? 'Unknown error';
        throw new Error(`Failed to update order status: ${errMsg}`);
      }

      if (!data) {
        throw new Error('Order status not found or access denied');
      }

      return orderStatusDbToClient(data as OrderStatusDb);
    },
    onSuccess: (updatedStatus) => {
      // Invalidate and refetch order statuses
      queryClient.invalidateQueries({ queryKey: orderStatusesKeys.all });
      toast.success(`Статус заказа "${updatedStatus.name}" обновлен`);
    },
    onError: (error: Error) => {
      console.error('Error updating order status:', error);
      toast.error(error.message || 'Ошибка при обновлении статуса заказа');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string): Promise<void> => {
      if (!user?.id) {
        throw new Error('User not authenticated');
      }

      const { error } = await supabaseWorker
        .from('order_statuses')
        .delete()
        .eq('id', id)
        .eq('user_id', user.id);

      if (error) {
        console.error('Error deleting order status:', error);
        const errMsg = (error as { message?: string | null } | null)?.message ?? 'Unknown error';
        throw new Error(`Failed to delete order status: ${errMsg}`);
      }
    },
    onSuccess: (_, deletedId) => {
      // Find the deleted status name for toast
      const deletedStatus = orderStatuses.find(status => status.id === deletedId);
      
      // Invalidate and refetch order statuses
      queryClient.invalidateQueries({ queryKey: orderStatusesKeys.all });
      toast.success(`Статус заказа "${deletedStatus?.name || ''}" удален`);
    },
    onError: (error: Error) => {
      console.error('Error deleting order status:', error);
      toast.error(error.message || 'Ошибка при удалении статуса заказа');
    },
  });

  const reorderMutation = useMutation({
    mutationFn: async (items: Array<{ id: string; sortOrder: number }>): Promise<void> => {
      if (!user?.id) {
        throw new Error('User not authenticated');
      }

      // Update each item's sort_order directly via Supabase
      const updates = items.map(item =>
        supabaseWorker
          .from('order_statuses')
          .update({ sort_order: item.sortOrder })
          .eq('id', item.id)
          .eq('user_id', user.id)
      );

      const results = await Promise.all(updates);
      
      // Check for errors
      const errors = results.filter(r => r.error);
      if (errors.length > 0) {
        console.error('Error reordering order statuses:', errors);
        throw new Error('Failed to reorder some items');
      }
    },
    onSuccess: () => {
      // Invalidate and refetch order statuses
      queryClient.invalidateQueries({ queryKey: orderStatusesKeys.all });
      toast.success('Порядок статусов обновлен');
    },
    onError: (error: Error) => {
      console.error('Error reordering order statuses:', error);
      toast.error(error.message || 'Ошибка при изменении порядка статусов');
    },
  });

  // =============================================================================
  // COMPUTED VALUES
  // =============================================================================

  const options: DomainStatusOption[] = statusesToOptions(orderStatuses);

  const defaultStatus = orderStatuses.find(status => 
    status.name.toLowerCase() === 'активный' || 
    status.name.toLowerCase() === 'active'
  );

  // =============================================================================
  // RETURN INTERFACE
  // =============================================================================

  return {
    // Data
    orderStatuses,
    options,
    defaultStatus,
    
    // State
    isLoading,
    error,
    
    // Actions
    refetch,
    create: createMutation.mutate,
    update: updateMutation.mutate,
    delete: deleteMutation.mutate,
    reorder: reorderMutation.mutate,
    
    // Mutation states
    isCreating: createMutation.isPending,
    isUpdating: updateMutation.isPending,
    isDeleting: deleteMutation.isPending,
    isReordering: reorderMutation.isPending,
    
    // Mutation errors
    createError: createMutation.error,
    updateError: updateMutation.error,
    deleteError: deleteMutation.error,
    reorderError: reorderMutation.error,
  };
}

// =============================================================================
// UTILITY HOOKS
// =============================================================================

/**
 * Hook for getting order status options for selectors
 */
export function useOrderStatusOptions(filters?: DomainStatusFilters) {
  const { options, isLoading, error } = useOrderStatuses({ filters });
  
  return {
    options,
    isLoading,
    error,
  };
}

/**
 * Hook for getting a single order status by ID
 */
export function useOrderStatus(id: string | undefined) {
  const { user } = useAuth();
  
  return useQuery({
    queryKey: orderStatusesKeys.detail(id || ''),
    queryFn: async (): Promise<OrderStatus | null> => {
      if (!user?.id || !id) {
        return null;
      }

      const { data, error } = await supabaseWorker
        .from('order_statuses')
        .select('*')
        .eq('id', id)
        .eq('user_id', user.id)
        .single();

      if (error) {
        const code = (error as { code?: string } | null)?.code;
        if (code === 'PGRST116') {
          return null; // Not found
        }
        const errMsg = (error as { message?: string } | null)?.message ?? 'Unknown error';
        throw new Error(`Failed to fetch order status: ${errMsg}`);
      }

      return orderStatusDbToClient(data as OrderStatusDb);
    },
    enabled: !!user?.id && !!id,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}
